import { useEffect, useState, useRef } from 'react'
import Bugsnag from '@bugsnag/js'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'

import { Box, Typography, Icon } from '@mui/material'

import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'
import { _convertAPIDate, _calculateWorkExperienceYear } from 'services/helper/DateTime'
import { postEmploymentData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object({
  employment_type: Yup.number().nullable().required('Employment Type is required'),
  hiring_date: Yup.date().nullable().required('Hiring Date is required'),
  commencement_date: Yup.date()
    .nullable()
    .min(Yup.ref('hiring_date'), "Commencement Date can't be before Hiring Date")
    .required('Commencement Date is required'),
})

const BasicEmploymentDetails = ({ data, employeeID, windowCheckEmployeeFile }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const [disable, setDisable] = useState(true)
  const [initialValues, setInitialValues] = useState({
    employment_type: '',
    hiring_date: '',
    commencement_date: '',
  })

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  const mutation = useMutation((value) => postEmploymentData(employeeID, value), {
    onSuccess: () => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeeEmploymentData', employeeID])
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitBasicEmployment = (values, actions) => {
    let newVal = {
      ...values,
      hiring_date: _convertAPIDate(values.hiring_date),
      commencement_date: _convertAPIDate(values.commencement_date),
    }
    mutation.mutate(newVal)
  }

  useEffect(() => {
    if (data) {
      setInitialValues({
        employment_type: data?.data?.employment_type_id,
        hiring_date: data?.data?.hiring_date,
        commencement_date: data?.data?.commencement_date,
      })
    }
  }, [data])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitBasicEmployment}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      innerRef={formikRef}
    >
      {({ values, touched, errors, handleChange, handleSubmit, setFieldValue, resetForm }) => (
        <Form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              alignItems: 'center',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Typography color="light-gray-text.main" fontWeight={500}>
              Basic employment details
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              borderBottom: windowCheckEmployeeFile && '1px solid',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Box width={{ xs: '100%' }}>
              {/* Employment Type */}
              <AdxDropdown
                placeholder="Employment type"
                label="Employment type"
                disabled={disable}
                sxLabel={{
                  color: disable ? 'light-gray-text.main' : 'dark-text.main',
                }}
                sxDropdown={{
                  color: 'light-gray-text.main',
                  backgroundColor: 'white.main',
                }}
                value={values.employment_type}
                name="employment_type"
                onChange={handleChange}
                errorMessage={<ErrorMessage name="employment_type" component={AdxErrorMessage} />}
                isErrorShown={touched.employment_type && errors.employment_type}
              >
                <AdxMenuItem value={1} selectedvalue={values.employment_type}>
                  {'Full time'}
                </AdxMenuItem>
                <AdxMenuItem value={2} selectedvalue={values.employment_type}>
                  {'Intern'}
                </AdxMenuItem>
                <AdxMenuItem value={3} selectedvalue={values.employment_type}>
                  {'Contract'}
                </AdxMenuItem>
              </AdxDropdown>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'start',
                gap: 2,
                width: '100%',
              }}
            >
              <Box width={{ xs: '100%', sm: '40%' }}>
                <AdxDatePicker
                  label="Hiring date"
                  disabled={disable}
                  onChange={(e) => setFieldValue(`hiring_date`, e)}
                  value={values.hiring_date}
                  name="hiring_date"
                  disableFuture
                  errorMessage={<ErrorMessage name="hiring_date" component={AdxErrorMessage} />}
                  isErrorShown={touched.hiring_date && errors.hiring_date}
                  sxLabel={{
                    color: disable ? '#8a8a8a' : 'dark-text.main',
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <AdxDatePicker
                  label="Commencement date"
                  disabled={disable}
                  onChange={(e) => setFieldValue(`commencement_date`, e)}
                  value={values.commencement_date}
                  name="commencement_date"
                  disableFuture
                  sx={{ width: 3 / 4 }}
                  sxLabel={{
                    color: disable ? '#8a8a8a' : 'dark-text.main',
                  }}
                  errorMessage={
                    <ErrorMessage name="commencement_date" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.commencement_date && errors.commencement_date}
                />
                <Typography
                  sx={{ width: 1 / 4, pt: 3 }}
                  fontWeight={400}
                  color="light-gray-text.main"
                >
                  {_calculateWorkExperienceYear(values.commencement_date)}
                </Typography>
              </Box>
            </Box>
            {windowCheckEmployeeFile && !disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  columnGap: 1,
                }}
              >
                <AdxButton
                  variant={'outlined'}
                  onClick={() => cancelEditForm(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  onClick={() => handleCheckRef(handleSubmit)}
                  sx={{ width: 0.2 }}
                >
                  Save
                </AdxButton>
              </Box>
            )}
          </Box>
          {windowCheckEmployeeFile && disable && (
            <Box sx={{ p: 0.5 }}>
              <AdxButton
                variant={'ghost'}
                sx={{
                  pl: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onClick={() => setDisable(false)}
              >
                <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                  Edit basic employment details
                </Typography>
              </AdxButton>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default BasicEmploymentDetails
